import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { nanoid } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LinesEllipsis from 'react-lines-ellipsis';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { imageBase, itemsClient } from '../../api/client';
import { QueryItemStatusChange } from '../../api/schema';
import { CardHeaderFlex } from '../../component/cardHeaderFlex/CardHeaderFlex';
import DependenciesButton from '../../component/dependenciesButton/DependenciesButton';
import ImageZoom from '../../component/imageZoom/ImageZoom';
import { InfoPopup } from '../../component/infoPopup/InfoPopup';
import LinearProgress from '../../component/linearProgress/LinearProgress';
import ListTypography from '../../component/listTypography/ListTypography';
import { MaintenanceForm } from '../../component/maintenance/MaintenanceForm';
import { CommonMap } from '../../component/map/Common';
import Skeleton from '../../component/skeleton/Skeleton';
import TabPanel from '../../component/tabPanel/TabPanel';
import { DeliverForm } from '../../component/tools/DeliverForm';
import { ToolForm } from '../../component/tools/ToolForm';
import { datesRange, lastSeenAt, tagsDate } from '../../helpers/dataHelper';
import useBooleanKeys from '../../hooks/useBooleanKeys';
import { useIsRemediation } from '../../hooks/useIsRemediation';
import { useMarketTerm } from '../../hooks/useMarketTerm';
import useSearchParams from '../../hooks/useSearchParams';
import { getImagesByOwnerId, listImagesByOwnerId } from '../../redux/slices/images/imagesSlice';
import { getMaintenanceEvents, getMaintenanceEventsByIdStats } from '../../redux/slices/maintenance/maintenanceSlice';
import {
  getToolById,
  getToolDependencies,
  getToolDependenciesById,
  getToolDependenciesFetching,
  getToolDetailsById,
  productFetching,
  removeTool,
  updateTool,
} from '../../redux/slices/tools/toolsSlice';
import { getUserAbilities } from '../../redux/slices/users/usersSlice';
import { useDispatch } from '../../redux/store';
import { Card, CardActions, CardFlex, CardHeaderTabs, GridCells, MediaHolder } from '../../styled/components';
import { ToolUsageContainer } from '../locationVisits/ToolUsageContainer';
import { ItemUtilization } from './ItemUtilization';
import MaintenanceEventsTab from './MaintenanceEventsTab';
import ReadSummaryTab from './ReadSummaryTab';
import { TagHealthTab } from './TagHealthTab';

type ToolDetailsProps = { type?: 'tools' | 'stock' };

const LinkColor = styled(Link).withConfig({
  shouldForwardProp: (prop) => !['outdated'].includes(prop),
})<{
  color?: 'error' | 'success' | 'warning';
}>`
  color: ${(props) => {
    return props.color && `${props.theme.palette[props.color].main} !important`;
  }};
`;

const SCAN_STATUS_AT_HOME = 0;
const SCAN_STATUS_IN_USE = 2;
const SCAN_STATUS_LEFT_ONSITE = 3;
const SCAN_STATUS_LOST = 4;
const SCAN_STATUS_RETIRED = 7;

// Manual status changes (Admin / Supervisor): what the menu offers and how the history reads.
type StatusAction = 'lost' | 'retire' | 'restore';
const STATUS_ACTION = {
  lost: {
    title: 'Mark as Lost?',
    confirm: 'Mark Lost',
    text: 'The item leaves the counts but keeps its history. The next scan that sees it brings it back where it was seen and lists it as Found.',
  },
  retire: {
    title: 'Retire this item?',
    confirm: 'Retire',
    text: 'The item is taken out of service: it leaves every list and scans of it are ignored until it is returned to service.',
  },
  restore: {
    title: 'Return to service?',
    confirm: 'Return to service',
    text: 'The item is back in service, at home and not tracked, until the next scan places it.',
  },
};
const RETIRE_REASONS: { id: string; label: string }[] = [
  { id: 'broken', label: 'Broken' },
  { id: 'worn_out', label: 'Worn out' },
  { id: 'sold', label: 'Sold' },
  { id: 'scrapped', label: 'Scrapped' },
  { id: 'stolen', label: 'Stolen' },
  { id: 'other', label: 'Other' },
];
const HISTORY_LABEL: Record<string, string> = { lost: 'Marked Lost', retired: 'Retired', returned: 'Returned to service' };
const reasonLabel = (id?: string | null): string => RETIRE_REASONS.find((reason) => reason.id === id)?.label ?? id ?? '';

export const ToolDetails: React.FC<ToolDetailsProps> = ({ type }: ToolDetailsProps) => {
  const term = useMarketTerm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, tab = 0 } = useParams();
  const item = useSelector(getToolDetailsById(id));
  const isRemediation = useIsRemediation();
  // tracking_site_from/to is the visit of the item's last scan. For a unit in a
  // room that is the room's own never-ending visit, so on Remediation the range
  // is shown only while the unit is on a job site (in use / left onsite), and a
  // unit at home shows when it came back (its last recovery) instead.
  const onsiteRange = item?.tracking_site_from && item?.tracking_site_to ? `Onsite: (${datesRange(item.tracking_site_from, item.tracking_site_to)})` : '';
  let siteStayText = onsiteRange;
  if (isRemediation) {
    if (item?.scan_status === SCAN_STATUS_IN_USE || item?.scan_status === SCAN_STATUS_LEFT_ONSITE) {
      siteStayText = onsiteRange;
    } else if (item?.scan_status === SCAN_STATUS_AT_HOME && item?.at_home_since) {
      siteStayText = `At home since ${tagsDate(item.at_home_since)}`;
    } else {
      siteStayText = '';
    }
  }
  const itemFetching = useSelector(productFetching);
  const maintenanceEventsStats = useSelector(getMaintenanceEventsByIdStats({ itemId: id || '', options: { fullList: true } }));
  const imageKeys = useSelector(getImagesByOwnerId(id));
  const dependencies = useSelector(getToolDependenciesById(id));
  const fetchingDependencies = useSelector(getToolDependenciesFetching);
  const [deleting, setDeleting] = useState(false);
  const [modals, toggleModal] = useBooleanKeys({ showEditProductItem: false, showMaintenanceForm: false, showDeliverForm: false });
  const [popupOpen, setPopupOpen] = useState('');
  const [maintenanceEventsKey, setMaintenanceEventsKey] = useState(nanoid());
  const abilities = useSelector(getUserAbilities);
  const [clamped, setClamped] = useState(false);
  const [, , clearParams] = useSearchParams();
  // Lost / Retire / Return to service: the menu, the open dialog and its fields, and the item's history.
  const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);
  const [statusAction, setStatusAction] = useState<StatusAction | ''>('');
  const [retireReason, setRetireReason] = useState('broken');
  const [statusNote, setStatusNote] = useState('');
  const [statusSending, setStatusSending] = useState(false);
  const [statusHistory, setStatusHistory] = useState<QueryItemStatusChange[]>([]);

  const tabValue = Number(tab);
  const fetching = itemFetching;

  // TODO refactor with RTK Api

  useEffect(() => {
    if (id) {
      dispatch(getMaintenanceEvents({ itemId: id, options: { fullList: true } }));
    }
  }, [dispatch, id]);

  const handleGetToolById = useCallback(() => {
    if (id) {
      dispatch(getToolById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      dispatch(listImagesByOwnerId(id));
      if (abilities.can('edit')) {
        dispatch(getToolDependencies(id));
      }
    }
  }, [dispatch, id, abilities]);

  useEffect(() => {
    handleGetToolById();
  }, [handleGetToolById]);

  const loadStatusHistory = useCallback(() => {
    if (id && abilities.can('CRUD')) {
      itemsClient
        .itemsStatusHistory(id)
        .then((response) => setStatusHistory(response.data.result ?? []))
        .catch(() => setStatusHistory([]));
    }
  }, [id, abilities]);

  useEffect(() => {
    loadStatusHistory();
  }, [loadStatusHistory]);

  const canMarkLost = item && item.scan_status !== SCAN_STATUS_LOST && item.scan_status !== SCAN_STATUS_RETIRED;
  const canRetire = item && item.scan_status !== SCAN_STATUS_RETIRED;
  const canRestore = item && (item.scan_status === SCAN_STATUS_LOST || item.scan_status === SCAN_STATUS_RETIRED);

  const openStatusDialog = (action: StatusAction) => {
    setStatusAnchor(null);
    setStatusNote('');
    setStatusAction(action);
  };

  const handleStatusChange = async () => {
    if (!item?.item_id || !statusAction) {
      return;
    }
    setStatusSending(true);
    try {
      await itemsClient.itemsChangeStatus({
        item_ids: [item.item_id],
        action: statusAction,
        ...(statusAction === 'retire' ? { reason: retireReason } : {}),
        ...(statusNote.trim() ? { note: statusNote.trim() } : {}),
      });
      setStatusAction('');
      handleGetToolById();
      loadStatusHistory();
    } finally {
      setStatusSending(false);
    }
  };

  const ownerLink = useMemo(() => {
    const linkByName = {
      Vehicle: `/company/vehicles/${item?.owner_id}/details`,
      Operator: `/company/users/${item?.owner_id}/details`,
      Room: `/company/rooms/${item?.owner_id}/details`,
    };

    return item?.owner_type_name && linkByName[item?.owner_type_name] && item?.owner_id && item?.owner_id !== '00000000-0000-0000-0000-000000000000' ? (
      <Link to={linkByName[item?.owner_type_name]}>Assigned to {item?.owner_name}</Link>
    ) : (
      item?.owner_name
    );
  }, [item]);

  const disableDelete = fetching || fetchingDependencies;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (dependencies?.connected_record_count && dependencies?.connected_record_count > 0) {
        const payload = {
          id: item?.item_id,
          tags: item?.tags?.filter((tag) => tag.rfid).map((tag) => tag.rfid || ''),
          identifier: item?.identifier,
          product_id: item?.product_id,
          warehouse_id: item?.owner_warehouse_id,
          active: false,
        };
        await dispatch(updateTool(payload)).unwrap();
      } else if (item?.item_id) {
        await itemsClient.deleteItem(item.item_id);
        dispatch(removeTool(item.item_id));
      }
      navigate(type === 'stock' ? '/stock' : '/tools');
    } catch (error) {
      setDeleting(false);
      console.log(error); // eslint-disable-line
    }
  };

  const handleChangeTab = (event, newValue) => {
    navigate(`/${type === 'stock' ? 'stock' : 'tools'}/items/${id}/details/${newValue}${newValue === 2 ? '?processedBegin=monday' : clearParams}`);
  };

  const address = useMemo(() => {
    const etc = [
      ...(item?.tracking_site_city ? [item?.tracking_site_city] : []),
      ...(item?.tracking_site_state ? [item?.tracking_site_state] : []),
      ...(item?.tracking_site_postal ? [item?.tracking_site_postal] : []),
    ].join(', ');
    const items = [...(item?.tracking_site_address1 ? [item?.tracking_site_address1] : []), ...(etc ? [etc] : [])];
    return items.map((element, index) => <div key={index.toString()}>{element}</div>);
  }, [item]);

  const handleCloseMaintenanceForm = (created?: boolean) => {
    toggleModal('showMaintenanceForm');
    if (created) {
      setMaintenanceEventsKey(nanoid());
      dispatch(getMaintenanceEvents({ itemId: id, options: { fullList: true } }));
    }
  };
  const handleReflow = ({ clamped }) => {
    setClamped(clamped);
  };

  const detailsData = [
    {
      label: 'Product',
      value: <Link to={`/${type}/products/${item?.product_id}/details`}>{item?.product_name}</Link>,
    },
    {
      label: 'Manufacturer',
      value: abilities.can('Container') ? (
        item?.manufacturer_name
      ) : (
        <Link to={`/${type}/manufacturers/${item?.manufacturer_id}/details`}>{item?.manufacturer_name}</Link>
      ),
    },
    ...(type === 'stock'
      ? [
          {
            label: 'Assigned Site',
            value: <Link to={`/customers/job-sites/${item?.assignment_site_id}/details`}>{item?.assignment_site_name}</Link>,
          },
        ]
      : []),
    ...(!isEmpty(item?.tags) || fetching
      ? [
          {
            label: (
              <Tooltip
                title={
                  <>
                    {item?.tags?.map((tag) => (
                      <Box sx={{ mb: 1 }} key={tag.id}>
                        {tag.rfid}
                      </Box>
                    ))}
                  </>
                }
                arrow
                placement="top"
              >
                <Chip sx={{ mb: 2 }} label={`${item?.tags ? item?.tags.length : 0} RFID ${pluralize('Tag', item?.tags ? item?.tags.length : 0)}`} />
              </Tooltip>
            ),
            value: (
              <Tooltip title={clamped && item?.notes ? item?.notes : ''}>
                <span>
                  <LinesEllipsis text={item?.notes ? `Notes: ${item?.notes}` : ''} maxLine={3} onReflow={handleReflow} />
                </span>
              </Tooltip>
            ),
            ownValue: true,
          },
        ]
      : []),
    {
      label: 'Warehouse',
      value: ownerLink,
    },
    {
      // Status and Created share a row: both are one short fact each, and the
      // card has more useful things to spend vertical space on.
      label: 'Status',
      value: (
        <Stack direction="row" justifyContent="space-between" spacing={4}>
          <Box component="span">{item?.active ? 'Is Active' : 'Is Disabled'}</Box>
          <Box component="span" sx={{ color: 'text.secondary' }}>
            Created {item?.created_at ? DateTime.fromISO(item?.created_at).toLocaleString(DateTime.DATE_MED) : ''}
          </Box>
        </Stack>
      ),
    },
    ...(abilities.can('Roll-Off')
      ? [
          {
            label: 'Born On',
            value: item?.born_on ? DateTime.fromISO(item?.born_on).toLocaleString(DateTime.DATE_MED) : '',
          },
        ]
      : []),
  ];

  return (
    <>
      {fetching && <LinearProgress />}
      <GridCells container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <CardFlex>
            <CardHeaderFlex title={<Skeleton randomWidth fetching={fetching} value={item?.identifier} width={100} />} legend="Details" />
            <CardHeader
              action={
                <Grid container display="flex" justifyContent="flex-end">
                  {abilities.can('CRUD') && (
                    <Grid item xs={12} md="auto" display="flex" justifyContent="flex-end" sx={{ pb: 2, pr: 2 }}>
                      <Button
                        onClick={(event) => setStatusAnchor(event.currentTarget)}
                        variant="outlined"
                        color={item?.scan_status === SCAN_STATUS_LOST || item?.scan_status === SCAN_STATUS_RETIRED ? 'warning' : 'primary'}
                        disabled={fetching || !item}
                        style={{ minWidth: 80 }}
                        data-testid="item-status-menu"
                      >
                        Status ▾
                      </Button>
                      <Menu anchorEl={statusAnchor} open={!!statusAnchor} onClose={() => setStatusAnchor(null)}>
                        {canMarkLost && <MenuItem onClick={() => openStatusDialog('lost')}>Mark Lost</MenuItem>}
                        {canRetire && <MenuItem onClick={() => openStatusDialog('retire')}>Retire…</MenuItem>}
                        {canRestore && <MenuItem onClick={() => openStatusDialog('restore')}>Return to service</MenuItem>}
                      </Menu>
                    </Grid>
                  )}
                  {abilities.can('CRUD') && (
                    <Grid item xs={12} md="auto" display="flex" justifyContent="flex-end" sx={{ pb: 2 }}>
                      <Button
                        onClick={() => toggleModal('showEditProductItem')}
                        variant="contained"
                        color="primary"
                        disabled={fetching}
                        style={{ minWidth: 80 }}
                      >
                        Edit
                      </Button>
                    </Grid>
                  )}
                  {abilities.can('CRUD') && (
                    <Grid item xs={12} md="auto" display="flex" justifyContent="flex-end">
                      <DependenciesButton
                        disableDelete={disableDelete}
                        dependencies={dependencies?.connected_record_count || 0}
                        active={item?.active}
                        deleting={deleting}
                        name={item?.identifier}
                        handleDelete={handleDelete}
                      />
                    </Grid>
                  )}
                </Grid>
              }
              title={<ListTypography data={detailsData} fetching={fetching} />}
            />
          </CardFlex>
        </Grid>
        <Grid item xs={12} sm={6}>
          {!!id && <ItemUtilization itemId={id} />}
        </Grid>
        <Grid item xs={12} sm={6}>
          <CardFlex>
            <CardHeaderFlex title={<Skeleton width={50} fetching={fetching} value={item?.scan_status_name && term(item.scan_status_name)} />} legend="Status" />
            {item?.assignment_site_name && (
              <Typography component="div" sx={{ ml: 4, mt: 2, mb: 2 }}>
                <Link to={`/customers/job-sites/${item?.assignment_site_id}/details/4`} style={{ fontWeight: 'bold' }}>
                  Assigned to {item?.assignment_site_name}
                </Link>
              </Typography>
            )}
            {(!!item?.tracking_site_name || !isEmpty(address) || !!item?.status_image || fetching) && (
              <CardHeader
                action={
                  item?.status_image && (
                    <Grid container justifyContent="flex-end">
                      <Grid item>
                        <Box
                          component="i"
                          className={clsx({
                            'tags-icon-work': item?.status_image === 'jobsite',
                            'tags-icon-home': item?.status_image === 'home',
                            'tags-icon-on-road': item?.status_image === 'road',
                            'tags-icon-question': item?.status_image === 'question',
                          })}
                          sx={{ fontSize: 48 }}
                        />
                      </Grid>
                    </Grid>
                  )
                }
                title={
                  (item?.tracking_site_name || address || fetching) && (
                    <>
                      <Typography variant="h4" component="div" sx={{ mt: 2, mb: 2 }}>
                        <Skeleton
                          randomWidth
                          fetching={fetching}
                          value={<Link to={`/customers/job-sites/${item?.tracking_site_id}/details`}>{item?.tracking_site_name}</Link>}
                          width={100}
                        />
                      </Typography>
                      <Typography component="div" sx={{ marginLeft: 4 }}>
                        <Skeleton randomWidth fetching={fetching} value={<strong>{address}</strong>} />
                      </Typography>
                    </>
                  )
                }
              />
            )}
            <CardContent sx={{ pt: 0, pb: 0 }}>
              <Typography component="div" sx={{ mb: 2 }}>
                <Skeleton
                  randomWidth
                  fetching={fetching}
                  value={
                    item?.processed_at && item?.payload_id !== '00000000-0000-0000-0000-000000000000' ? (
                      <Link to={`/payloads/${item?.payload_id}/details`}>Last Seen: {lastSeenAt(item?.processed_at)}</Link>
                    ) : (
                      ''
                    )
                  }
                />
              </Typography>
              {statusHistory.length > 0 && (
                <Box sx={{ mb: 2 }} data-testid="item-status-history">
                  {statusHistory.slice(0, 5).map((change) => (
                    <Typography key={change.id} variant="body2" color="text.secondary">
                      {HISTORY_LABEL[change.action ?? ''] ?? change.action} by {change.user_name}
                      {change.created_at ? ` · ${DateTime.fromISO(change.created_at).toLocaleString(DateTime.DATETIME_SHORT)}` : ''}
                      {change.reason ? ` · ${reasonLabel(change.reason)}` : ''}
                      {change.note ? ` · ${change.note}` : ''}
                    </Typography>
                  ))}
                </Box>
              )}
              <Divider sx={{ my: 4, borderWidth: 1 }} />
              <Grid container>
                <Grid item xs>
                  <Typography variant="h4" component="div" sx={{ mb: 2 }}>
                    <Skeleton
                      randomWidth
                      fetching={fetching}
                      value={
                        item?.payload_warehouse_name && item?.owner_id && item?.owner_id !== '00000000-0000-0000-0000-000000000000' ? (
                          <Link to={`${item?.owner_type_id === 1 ? `/company/rooms/${item?.owner_id}` : `/company/vehicles/${item?.owner_id}`}/details`}>
                            {item?.payload_warehouse_name}
                          </Link>
                        ) : (
                          item?.payload_warehouse_name || ''
                        )
                      }
                    />
                  </Typography>
                  <Typography component="div" sx={{ mb: 2 }}>
                    <Skeleton randomWidth fetching={fetching} value={siteStayText} />
                  </Typography>
                </Grid>
                <Grid item>
                  <Skeleton
                    randomWidth
                    fetching={fetching}
                    width={100}
                    value={
                      <Box>
                        <LinkColor to={`/tools/items/${id}/details/1`} {...(maintenanceEventsStats.outdated && { color: 'error' })}>
                          {maintenanceEventsStats.open
                            ? `${maintenanceEventsStats.open} ${pluralize('Maintenance Event', maintenanceEventsStats.open)}`
                            : 'No Maintenance Events'}
                        </LinkColor>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
            <CardActions>
              <Grid container justifyContent="space-between">
                <Grid item>
                  <Button onClick={() => toggleModal('showDeliverForm')} variant="contained" color="primary" disabled={fetching} sx={{ ml: 2, mb: 2 }}>
                    Deliver
                  </Button>
                </Grid>
                <Grid item>
                  <IconButton onClick={() => setPopupOpen('status')}>
                    <InfoOutlinedIcon fontSize="small" color="secondary" />
                  </IconButton>
                </Grid>
              </Grid>
            </CardActions>
          </CardFlex>
        </Grid>
        <Grid item xs={12} sm={6}>
          <CardFlex style={{ display: 'flex', flexDirection: 'column' }}>
            <CardHeaderFlex
              title={<Skeleton randomWidth fetching={fetching} value={item?.image_src || 'unknown'} width={100} />}
              legend={<ImageZoom title={item?.identifier} label="Image Source" image={imageKeys?.[0]} />}
            />
            <MediaHolder className={clsx({ icon: imageKeys.length === 0 })} sx={{ p: 4 }}>
              {imageKeys.length > 0 ? (
                <CardMedia component="img" image={imageBase(imageKeys[0])} alt={item?.product_name} style={{ objectFit: 'contain' }} />
              ) : (
                <ImageOutlinedIcon sx={{ fontSize: '72px', opacity: 0.1 }} />
              )}
            </MediaHolder>
          </CardFlex>
        </Grid>
        <Grid item xs={12}>
          <CardFlex>
            <CardHeaderFlex
              title={<Skeleton width={50} fetching={fetching} value={item?.tracking_site_name || <span>&nbsp;</span>} />}
              legend="Last Location"
            />
            {item && !!item?.latitude && !!item?.longitude && (
              <CommonMap
                zoom={17}
                markers={[
                  {
                    key: item?.item_id || '',
                    lat: item?.latitude || 0,
                    lng: item?.longitude || 0,
                    locationType: item?.scan_status_name,
                    vehicleName: item?.latitude && item?.longitude ? item?.tracking_vehicle_name : 'Location Unknown',
                    siteName: item?.tracking_site_name,
                    siteId: item?.tracking_site_id,
                    locationVisitId: item?.location_visit_id,
                  },
                ]}
                mobileView
                withTrafficFullScreen
              />
            )}
            {item && !item?.latitude && !item?.longitude && (
              <CardFlex style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }} sx={{ p: 4 }}>
                <Typography variant="h4" component="div">
                  Never Seen
                </Typography>
              </CardFlex>
            )}
          </CardFlex>
        </Grid>
      </GridCells>
      <Card mb={6}>
        <CardHeaderTabs
          title={
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleChangeTab} variant="scrollable" scrollButtons="auto">
                <Tab label="Activity" />
                {!abilities.can('Stock') && <Tab label="Maintenance Events" />}
                <Tab label="History" />
                <Tab label="Tag Health" />
              </Tabs>
            </Box>
          }
          action={
            <>
              {tabValue === 1 && abilities.can('create') && (
                <Button variant="contained" sx={{ mr: 2 }} onClick={() => toggleModal('showMaintenanceForm')}>
                  New
                </Button>
              )}
              <IconButton onClick={() => setPopupOpen('tabs')}>
                <InfoOutlinedIcon fontSize="small" color="secondary" />
              </IconButton>
            </>
          }
          sx={{
            '& .MuiCardHeader-content': {
              width: 0,
            },
          }}
        />
        <CardContent sx={{ p: 0 }}>
          <TabPanel value={tabValue} index={0}>
            <ToolUsageContainer withExport={false} itemId={id} withFilters />
          </TabPanel>
          {!abilities.can('Stock') && (
            <TabPanel value={tabValue} index={1}>
              <MaintenanceEventsTab key={maintenanceEventsKey} />
            </TabPanel>
          )}
          <TabPanel value={tabValue} index={abilities.can('Stock') ? 1 : 2}>
            <ReadSummaryTab />
          </TabPanel>
          <TabPanel value={tabValue} index={abilities.can('Stock') ? 2 : 3}>
            <TagHealthTab disableSortBy />
          </TabPanel>
        </CardContent>
      </Card>
      <InfoPopup title="Status Details" subtitle="Displays status and usage about the Tool." onClose={() => setPopupOpen('')} open={popupOpen === 'status'}>
        <CardFlex>
          <CardHeader
            title={
              <>
                <Typography variant="h4" component="div">
                  Status
                </Typography>
                <Typography variant="h4" component="div" sx={{ mt: 2, mb: 2 }}>
                  <Link to="#">(Last Location)</Link>
                </Typography>
                <Typography component="div" sx={{ marginLeft: 4, mb: 2 }}>
                  (Address if at site)
                </Typography>
              </>
            }
          />
          <CardContent sx={{ pt: 0, pb: 0 }}>
            <Typography component="div" sx={{ mb: 2 }}>
              <Link to="#">Last seen time</Link>
            </Typography>
            <Typography variant="h4" component="div" sx={{ mb: 2 }}>
              <Link to="#">(Vehicle item is tracked by)</Link>
            </Typography>
            <Typography component="div" sx={{ mb: 2 }}>
              Onsite
            </Typography>
          </CardContent>
          <CardActions>
            <IconButton onClick={() => setPopupOpen('status')}>
              <InfoOutlinedIcon fontSize="small" color="secondary" />
            </IconButton>
          </CardActions>
        </CardFlex>
      </InfoPopup>
      <InfoPopup title="Tab Details" subtitle="Displays the usage history of the Tool." onClose={() => setPopupOpen('')} open={popupOpen === 'tabs'}>
        <Card sx={{ mb: 3, p: 3 }}>
          <CardHeader title={<strong>Usage</strong>} sx={{ p: 0 }} />
          <CardContent sx={{ p: 0, pt: 2 }} style={{ paddingBottom: 0 }}>
            List of usage information for the item including:
            <ul>
              <li>Time period of the Site Visit</li>
              <li>Vehicle that used it</li>
              <li>Customer - Site combination where it was used</li>
            </ul>
          </CardContent>
        </Card>
      </InfoPopup>
      <ToolForm open={modals.showEditProductItem} onClose={() => toggleModal('showEditProductItem')} productToolId={id} type={type} />
      <MaintenanceForm
        open={modals.showMaintenanceForm}
        onClose={handleCloseMaintenanceForm}
        title={`${item?.identifier} — ${item?.product_name}`}
        itemId={item?.item_id}
      />

      <DeliverForm open={modals.showDeliverForm} onClose={() => toggleModal('showDeliverForm')} itemId={item?.item_id} identifier={item?.identifier} />
      <Dialog open={!!statusAction} onClose={() => !statusSending && setStatusAction('')} fullWidth maxWidth="xs" data-testid="item-status-dialog">
        <DialogTitle>{statusAction ? STATUS_ACTION[statusAction].title : ''}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{item?.identifier}</strong> — {statusAction ? STATUS_ACTION[statusAction].text : ''}
          </Typography>
          {statusAction === 'retire' && (
            <TextField select fullWidth size="small" label="Reason" value={retireReason} onChange={(e) => setRetireReason(e.target.value)} sx={{ mb: 2 }}>
              {RETIRE_REASONS.map((reason) => (
                <MenuItem key={reason.id} value={reason.id}>
                  {reason.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            fullWidth
            size="small"
            label="Note (optional)"
            value={statusNote}
            onChange={(e) => setStatusNote(e.target.value.slice(0, 500))}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusAction('')} disabled={statusSending}>
            Cancel
          </Button>
          <Button
            onClick={handleStatusChange}
            variant="contained"
            color={statusAction === 'restore' ? 'primary' : 'error'}
            disabled={statusSending}
            data-testid="item-status-confirm"
          >
            {statusAction ? STATUS_ACTION[statusAction].confirm : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
